import axiosInstance from '@/api/axiosInstance';
import type {
  CreateProductPayload,
  GetProductsParams,
  Product,
  ProductListResponse,
  UpdateProductPayload,
} from '@/types/products';

export interface ProductStock {
  id: number;
  branchId: number;
  productId: number;
  quantity: number;
  reservedQuantity: number;
  price?: string;
  createdAt: string;
  updatedAt: string;
}

export const productAPI = {
  getProducts: async (
    params: GetProductsParams = {}
  ): Promise<ProductListResponse> => {
    const response = await axiosInstance.get('/products', { params });
    return response.data as ProductListResponse;
  },
  getProductById: async (id: number | string): Promise<Product> => {
    const response = await axiosInstance.get(`/products/${id}`);
    return response.data as Product;
  },
  createProduct: async (
    payload: CreateProductPayload
  ): Promise<{ id: number } & Record<string, unknown>> => {
    const response = await axiosInstance.post('/products/simple', payload);
    return response.data;
  },

  // createSimpleProduct: async (
  //   payload: CreateProductPayload
  // ): Promise<{ id: number } & Record<string, unknown>> => {
  //   const response = await axiosInstance.post('/products/simple', payload);
  //   return response.data;
  // },
  updateProduct: async (
    id: number | string,
    payload: UpdateProductPayload
  ): Promise<Record<string, unknown>> => {
    const response = await axiosInstance.put(`/products/${id}`, payload);
    return response.data;
  },
  deleteProduct: async (
    id: number | string
  ): Promise<Record<string, unknown>> => {
    const response = await axiosInstance.delete(`/products/${id}`);
    return response.data;
  },

  // Stock management
  getProductStock: async (productId: number | string) => {
    const response = await axiosInstance.get(`/stock/product/${productId}`);
    return response.data;
  },

  updateProductStock: async (
    productId: number | string,
    data: {
      type: 'add' | 'remove';
      quantity: number;
      reason?: string;
    }
  ) => {
    const response = await axiosInstance.post(
      `/stock/product/${productId}/adjust`,
      data
    );
    return response.data;
  },

  updateProductPrice: async (
    productId: number | string,
    data: {
      newPrice: number;
      reason?: string;
    }
  ) => {
    const response = await axiosInstance.put(
      `/stock/product/${productId}/price`,
      data
    );
    return response.data;
  },

  // Image management
  uploadProductImages: async (
    images: File[],
    sortOrders?: number[]
  ): Promise<{ imageIds: string[] }> => {
    const formData = new FormData();

    images.forEach((image) => {
      formData.append('images', image);
    });

    if (sortOrders && sortOrders.length > 0) {
      formData.append('sortOrders', sortOrders.join(','));
    }

    const response = await axiosInstance.post('/products/images', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  reorderProductImages: async (
    imageIds: string[]
  ): Promise<{
    message: string;
    reorderedImages: { id: string; sortOrder: number }[];
  }> => {
    const response = await axiosInstance.put('/products/images/reorder', {
      imageIds,
    });
    return response.data;
  },

  deleteProductImage: async (imageId: string): Promise<void> => {
    await axiosInstance.delete(`/product-images/${imageId}`);
  },

  // Upload single product image
  uploadProductImage: async (
    file: File,
    sortOrder?: number
  ): Promise<{
    id: string;
    originalUrl: string;
    thumbnailUrl: string;
    sortOrder: number;
    createdAt: string;
    updatedAt: string;
  }> => {
    const formData = new FormData();
    formData.append('file', file);
    if (sortOrder !== undefined) {
      formData.append('sortOrder', sortOrder.toString());
    }

    const response = await axiosInstance.post('/product-images', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};
